import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface Birthday {
  id: string;
  firstName: string;
  lastName: string;
  type: 'student' | 'staff';
  date: string;
}

export const useBirthdays = () => {
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;

  return useQuery({
    queryKey: ['birthdays', organizationId],
    queryFn: async (): Promise<Birthday[]> => {
      try {
        if (!organizationId) return [];

      const now = new Date();
      const currentYear = now.getFullYear();

      console.log('Debug: Current date:', now);
      const birthdays: Birthday[] = [];

      // Fetch students with birthdays in the next 7 days
      const { data: students, error: studentsError } = await supabase
        .from('students')
        .select('id, first_name, last_name, date_of_birth')
        .eq('organization_id', organizationId)
        .not('date_of_birth', 'is', null);

      if (!studentsError && students) {
        students.forEach(student => {
          if (student.date_of_birth) {
            try {
              // Parse date as local date to avoid timezone issues
              const birthDateStr = student.date_of_birth;
              const [year, month, day] = birthDateStr.split('-').map(Number);
              
              // Validate parsed date components
              if (!month || !day || month < 1 || month > 12 || day < 1 || day > 31) {
                console.warn('Invalid birthday date for student:', student.id, birthDateStr);
                return;
              }
              
              const birthMonth = month;
              const birthDay = day;
            
            // Create this year's birthday date
            const thisYearBirthday = new Date(currentYear, birthMonth - 1, birthDay);
            
            // If this year's birthday has passed, check next year's birthday
            const nextYearBirthday = new Date(currentYear + 1, birthMonth - 1, birthDay);
            
            // Calculate days until this year's birthday
            const diffTimeThisYear = thisYearBirthday.getTime() - now.getTime();
            const daysUntilThisYear = Math.ceil(diffTimeThisYear / (1000 * 60 * 60 * 24));
            
            // Calculate days until next year's birthday
            const diffTimeNextYear = nextYearBirthday.getTime() - now.getTime();
            const daysUntilNextYear = Math.ceil(diffTimeNextYear / (1000 * 60 * 60 * 24));
            
            // Include if birthday is within next 7 days (this year or next year)
            if ((daysUntilThisYear >= 0 && daysUntilThisYear <= 7) || 
                (daysUntilThisYear < 0 && daysUntilNextYear <= 7)) {
              
              // Use the appropriate birthday date (this year or next year)
              const targetBirthday = daysUntilThisYear >= 0 ? thisYearBirthday : nextYearBirthday;
              
              birthdays.push({
                id: student.id,
                firstName: student.first_name,
                lastName: student.last_name,
                type: 'student',
                date: `${targetBirthday.getFullYear()}-${(targetBirthday.getMonth()+1).toString().padStart(2,'0')}-${targetBirthday.getDate().toString().padStart(2,'0')}`
              });
            }
            } catch (error) {
              console.error('Error processing student birthday:', student, error);
            }
          }
        });
      }

      // Fetch staff with birthdays in the next 7 days
      const { data: staff, error: staffError } = await supabase
        .from('staff_members')
        .select('id, first_name, last_name, birthday_month, birthday_day')
        .eq('organization_id', organizationId)
        .not('birthday_month', 'is', null)
        .not('birthday_day', 'is', null);

      if (!staffError && staff) {
        staff.forEach(member => {
          if (member.birthday_month && member.birthday_day) {
            try {
              const birthMonth = member.birthday_month;
              const birthDay = member.birthday_day;
              
              // Validate birthday values
              if (birthMonth < 1 || birthMonth > 12 || birthDay < 1 || birthDay > 31) {
                console.warn('Invalid birthday data for staff:', member.id, { birthMonth, birthDay });
                return;
              }
            
            // Create this year's birthday date
            const thisYearBirthday = new Date(currentYear, birthMonth - 1, birthDay);
            
            // If this year's birthday has passed, check next year's birthday
            const nextYearBirthday = new Date(currentYear + 1, birthMonth - 1, birthDay);
            
            // Calculate days until this year's birthday
            const diffTimeThisYear = thisYearBirthday.getTime() - now.getTime();
            const daysUntilThisYear = Math.ceil(diffTimeThisYear / (1000 * 60 * 60 * 24));
            
            // Calculate days until next year's birthday
            const diffTimeNextYear = nextYearBirthday.getTime() - now.getTime();
            const daysUntilNextYear = Math.ceil(diffTimeNextYear / (1000 * 60 * 60 * 24));
            
            // Include if birthday is within next 7 days (this year or next year)
            if ((daysUntilThisYear >= 0 && daysUntilThisYear <= 7) || 
                (daysUntilThisYear < 0 && daysUntilNextYear <= 7)) {
              
              // Use the appropriate birthday date (this year or next year)
              const targetBirthday = daysUntilThisYear >= 0 ? thisYearBirthday : nextYearBirthday;
              
              birthdays.push({
                id: member.id,
                firstName: member.first_name,
                lastName: member.last_name,
                type: 'staff',
                date: `${targetBirthday.getFullYear()}-${(targetBirthday.getMonth()+1).toString().padStart(2,'0')}-${targetBirthday.getDate().toString().padStart(2,'0')}`
              });
            }
            } catch (error) {
              console.error('Error processing staff birthday:', member, error);
            }
          }
        });
      }

      console.log('Debug: Final birthdays found:', birthdays.length);
      console.log('Debug: Birthday details:', birthdays);
      
      // Sort birthdays by date (today first, then chronological)
      return birthdays.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA.getTime() - dateB.getTime();
      });
      } catch (error) {
        console.error('Error fetching birthdays:', error);
        return [];
      }
    },
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 30, // Cache for 30 minutes
    refetchOnWindowFocus: false, // Prevent refetch on focus to avoid flickering
    refetchOnMount: true, // Refetch on mount to ensure birthdays show after refresh
  });
};